import { Helmet } from "react-helmet-async";
import type { ReactNode } from "react";
import SiteNavBar from "@/components/SiteNavBar";
import Footer from "@/components/Footer";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Monitor, MessageSquare } from "lucide-react";
import { LANDING_COLORS } from "@/lib/landing-styles";
import { RevealOnScroll, FloatingOrbs } from "@/components/landing";

// One list drives both the visible answers and the FAQPage JSON-LD, so the two
// cannot drift apart. A plain answer goes into the JSON-LD as it reads on the
// page; the one answer that renders as several paragraphs carries `schema`, a
// summary made only of its own visible sentences. SCHEMA_ORDER picks the five
// questions the JSON-LD has always carried, in their original order.
type FaqEntry = { value: string; q: string; a: ReactNode; schema?: string };

const GENERAL_FAQS: FaqEntry[] = [
  {
    value: "web-vs-whatsapp",
    q: "What's the difference between Fly Web Dashboard and Fly WhatsApp Agent?",
    a: (
      <div className="space-y-3">
        <p><strong className="dark:text-slate-200">Fly Web Dashboard</strong> is our full-featured DIY platform where you manage everything yourself through a powerful web interface. You get complete control with detailed analytics, multi-location management, advanced posting tools, and comprehensive review management.</p>
        <p><strong className="dark:text-slate-200">Fly WhatsApp Agent</strong> is a hands-free solution where our AI assistant handles everything for you via WhatsApp. Just message Fly to get updates, approve responses, or request reports. No dashboard, no apps, no logins required.</p>
        <p className="font-medium dark:text-slate-300">Choose Web Dashboard if you want full control and deep analytics. Choose WhatsApp Agent if you prefer a zero-effort, conversational experience.</p>
      </div>
    ),
    schema: "Fly Web Dashboard is our full-featured DIY platform where you manage everything yourself through a powerful web interface. Fly WhatsApp Agent is a hands-free solution where our AI assistant handles everything for you via WhatsApp. Choose Web Dashboard if you want full control and deep analytics. Choose WhatsApp Agent if you prefer a zero-effort, conversational experience.",
  },
  {
    value: "results",
    q: "How soon can I see results?",
    a: "Most specialists see measurable improvements in 45-60 days as your optimized profile gains traction and attracts more client attention.",
  },
  {
    value: "ownership",
    q: "Do I still own my Google profile?",
    a: "Yes, you always own your profile. We simply optimize and manage it on your behalf while you retain complete ownership and control.",
  },
  {
    value: "industries",
    q: "Which industries do you work with?",
    a: "Local businesses in every industry, including restaurants, retail stores, medical practices, salons, fitness centers and professional services. We tailor the work to each industry's needs.",
  },
  {
    value: "cancel",
    q: "Can I cancel anytime?",
    a: "Yes, with 30 days' notice. Your Google profile stays yours, and you keep every optimization we made.",
  },
];

const WEB_DASHBOARD_FAQS: FaqEntry[] = [
  {
    value: "web-features",
    q: "What features does the Web Dashboard include?",
    a: "The Web Dashboard includes comprehensive analytics and reporting, multi-location management, AI-powered post scheduling, review management with AI response suggestions, competitor tracking, Google Business Profile optimization tools, and detailed performance insights.",
  },
  {
    value: "web-technical",
    q: "Do I need technical knowledge to use the Dashboard?",
    a: "No. The dashboard is designed to be easy to use, and our onboarding tutorials and support help you get started quickly.",
  },
  {
    value: "web-locations",
    q: "Can I manage multiple locations?",
    a: "Yes. The Web Dashboard supports unlimited locations: manage them all from one dashboard, schedule posts across locations and see combined analytics.",
  },
  {
    value: "web-pricing",
    q: "What are the Web Dashboard pricing plans?",
    a: "Plans range from basic profile management to enterprise features. See our pricing page, or contact us for a quote based on your number of locations and the features you need.",
  },
];

const WHATSAPP_AGENT_FAQS: FaqEntry[] = [
  {
    value: "whatsapp-how",
    q: "How does the WhatsApp Agent work?",
    a: "Simply message Fly on WhatsApp like you would a colleague. Ask for your performance report, get notified about new reviews, approve AI-generated responses, and manage your profile, all through natural conversation. No apps to install, no dashboards to learn.",
  },
  {
    value: "whatsapp-features",
    q: "What can the WhatsApp Agent do for me?",
    a: "Real-time review alerts, AI-powered review responses, 24/7 profile monitoring, monthly performance reports, keyword insights and review generation tools. You can also ask for a report or change your preferences in the chat at any time.",
  },
  {
    value: "whatsapp-reviews",
    q: "How does Fly handle my reviews?",
    a: "When a new review arrives, Fly instantly sends it to you on WhatsApp with an AI-written draft reply. Approve it with one tap, edit it or write your own. For negative reviews, Fly alerts you at once so you can respond before it hurts your business.",
  },
  {
    value: "whatsapp-setup",
    q: "How long does setup take?",
    a: "Setup takes 5 minutes. Connect your Google Business Profile, save Fly's WhatsApp number and you're set. Fly starts monitoring your profile and learning your brand voice right away.",
  },
  {
    value: "whatsapp-pricing",
    q: "What does the WhatsApp Agent cost?",
    a: "Fly Agent Lite starts at $29 a month per location for the essential features. See our pricing page for every plan, or message us on WhatsApp to start a free trial.",
  },
  {
    value: "whatsapp-auto",
    q: "Can Fly respond to reviews automatically?",
    a: "Yes. Turn on auto-replies for positive reviews (4 to 5 stars) and keep approving negative ones yourself. Happy customers hear back at once, and you stay in control of the sensitive ones.",
  },
];

const ALL_FAQS = [...GENERAL_FAQS, ...WEB_DASHBOARD_FAQS, ...WHATSAPP_AGENT_FAQS];
const SCHEMA_ORDER = ["web-vs-whatsapp", "results", "ownership", "whatsapp-how", "web-features"];

const FAQ_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "name": "Google My Business Optimization FAQ",
  "description": "Frequently asked questions about Google My Business optimization and management services",
  "mainEntity": SCHEMA_ORDER.map((value) => {
    const entry = ALL_FAQS.find((f) => f.value === value)!;
    return {
      "@type": "Question",
      "name": entry.q,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": entry.schema ?? String(entry.a),
      },
    };
  }),
};

// A plain bordered row per question: no card inside the accordion item, no
// hover lift, the open row picks up the brand tint.
const FaqGroup = ({ items }: { items: FaqEntry[] }) => (
  <Accordion type="single" collapsible className="space-y-3">
    {items.map(({ value, q, a }) => (
      <AccordionItem
        key={value}
        value={value}
        className="rounded-xl border border-gray-200 bg-white px-6 transition-colors data-[state=open]:border-purple-300 dark:border-slate-700 dark:bg-slate-800/60 dark:data-[state=open]:border-purple-600"
      >
        <AccordionTrigger className="py-5 text-left text-base font-semibold hover:no-underline dark:text-slate-100">
          {q}
        </AccordionTrigger>
        <AccordionContent className="pb-5">
          <div className="text-sm text-gray-600 dark:text-slate-400 leading-relaxed">
            {typeof a === "string" ? <p>{a}</p> : a}
          </div>
        </AccordionContent>
      </AccordionItem>
    ))}
  </Accordion>
);

const FAQ = () => {
  return (
    <div className="min-h-screen font-['Inter']">
      <Helmet>
        <title>Google Business Profile FAQ | Setup, Pricing and Results</title>
        <meta name="description" content="Answers to the questions we get asked most about Google Business Profile optimization, local SEO services, pricing, setup time, and the results to expect." />
        <meta name="keywords" content="Google My Business optimization FAQ, GMB management questions, Google My Business setup FAQ, local SEO services FAQ, Google Business Profile help, GMB optimization answers, Google My Business verification FAQ" />
        <link rel="canonical" href="https://www.fly-social.com/faq" />
        <meta property="og:title" content="Google Business Profile FAQ | Setup, Pricing and Results" />
        <meta property="og:description" content="Get answers to frequently asked questions about Google My Business optimization, GMB management, local SEO services, pricing, setup time, and more." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.fly-social.com/faq" />
        <meta property="og:image" content="https://www.fly-social.com/lovable-uploads/ad42997b-b41b-49fe-8e06-dc69572da5bd.png" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Google Business Profile FAQ | Setup, Pricing and Results" />
        <meta name="twitter:description" content="Get answers to frequently asked questions about Google My Business optimization, GMB management, local SEO services, pricing, setup time, and more." />
        <meta name="twitter:image" content="https://www.fly-social.com/lovable-uploads/ad42997b-b41b-49fe-8e06-dc69572da5bd.png" />
        <script type="application/ld+json">{JSON.stringify(FAQ_SCHEMA)}</script>
      </Helmet>

      <SiteNavBar />

      {/* Hero */}
      <section
        className="pt-24 md:pt-32 pb-16 relative overflow-hidden dark:bg-slate-950"
        style={{ background: 'linear-gradient(135deg, #faf5ff 0%, #f0fdf4 40%, #ecfeff 100%)' }}
      >
        <FloatingOrbs variant="hero" />
        <div className="container mx-auto px-4 sm:px-6 relative z-10 text-center">
          <RevealOnScroll>
            <h1
              className="text-4xl md:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.1] mb-4 dark:text-white"
              style={{ color: LANDING_COLORS.text.primary }}
            >
              Everything You{' '}
              <span style={{ color: LANDING_COLORS.primary }}>Need to Know</span>
            </h1>
            <p
              className="text-lg md:text-xl max-w-2xl mx-auto dark:text-slate-400"
              style={{ color: LANDING_COLORS.text.secondary }}
            >
              Answers to common questions about growing your business with Fly.
            </p>
          </RevealOnScroll>
        </div>
      </section>

      {/* FAQ content. Shown at once, with no scroll-in, so a reader lands
          straight on the answers. */}
      <section className="py-16 bg-white dark:bg-slate-900">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="max-w-3xl mx-auto space-y-14">
            <div>
              <h2 className="text-2xl font-bold mb-6 dark:text-white" style={{ color: LANDING_COLORS.text.primary }}>
                General questions
              </h2>
              <FaqGroup items={GENERAL_FAQS} />
            </div>

            <div>
              <h2 className="flex items-center gap-2.5 text-2xl font-bold mb-6 dark:text-white" style={{ color: LANDING_COLORS.text.primary }}>
                <Monitor className="h-5 w-5 shrink-0" style={{ color: LANDING_COLORS.primary }} aria-hidden />
                Fly Web Dashboard
              </h2>
              <FaqGroup items={WEB_DASHBOARD_FAQS} />
            </div>

            <div>
              <h2 className="flex items-center gap-2.5 text-2xl font-bold mb-6 dark:text-white" style={{ color: LANDING_COLORS.text.primary }}>
                <MessageSquare className="h-5 w-5 shrink-0 text-green-700 dark:text-green-400" aria-hidden />
                Fly WhatsApp Agent
              </h2>
              <FaqGroup items={WHATSAPP_AGENT_FAQS} />
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default FAQ;
